/**
 * O painel sobreposto onde um registro se edita.
 *
 * **Por que ele existe.** O formulário nascia no topo da página, acima da
 * lista. Com 312 professores isso significava: clicar em "Editar" na linha
 * 180, a página pular para um formulário lá em cima, preencher, salvar, e
 * voltar para o topo da lista — com a linha 180 outra vez a 180 rolagens de
 * distância. O formulário não é uma seção da página: é uma interrupção dela,
 * e precisa parecer uma.
 *
 * **Gaveta e não caixa centrada.** O formulário do professor tem dezessete
 * campos, e abaixo dele vêm as relações e, no esquema, os horários. Numa
 * caixa centrada isso vira uma janelinha com barra de rolagem própria
 * espremida no meio da tela. A gaveta ocupa a altura inteira, que é a forma
 * natural de um formulário longo, e deixa a lista visível à esquerda — o que
 * mantém o contexto de onde a pessoa estava.
 *
 * **O que ele faz por acessibilidade**, e cada item responde a um jeito
 * conhecido de a pessoa ficar presa:
 *
 * - `role="dialog"` + `aria-modal` para o leitor de tela anunciar que o resto
 *   da página saiu de cena;
 * - Escape fecha, porque é a tecla que todo mundo tenta primeiro;
 * - o foco entra no painel ao abrir e **volta para onde estava** ao fechar —
 *   sem isso, quem usa teclado volta ao começo da página a cada edição;
 * - Tab circula dentro do painel, senão o foco escapa para a lista atrás do
 *   véu e a pessoa digita onde não vê.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react'

/** O que pode receber foco dentro do painel, na ordem em que o Tab anda. */
const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Painel({
  titulo,
  nota,
  aoFechar,
  children,
}: {
  titulo: string
  nota?: string
  aoFechar: () => void
  children: ReactNode
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const voltarPara = useRef<HTMLElement | null>(null)

  const tecla = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        aoFechar()
        return
      }
      if (e.key !== 'Tab' || !caixa.current) return
      const alvos = [...caixa.current.querySelectorAll<HTMLElement>(FOCAVEIS)]
      if (alvos.length === 0) return
      const primeiro = alvos[0]!
      const ultimo = alvos[alvos.length - 1]!
      // O ciclo é manual porque não há como pedir ao navegador que confine o
      // Tab a uma subárvore: `inert` resolveria, mas só a partir do momento
      // em que todo navegador do piloto o tiver.
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault()
        ultimo.focus()
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault()
        primeiro.focus()
      }
    },
    [aoFechar],
  )

  useEffect(() => {
    voltarPara.current = document.activeElement as HTMLElement | null
    window.addEventListener('keydown', tecla)
    // O foco vai para o painel, não para o primeiro campo: anunciar o título
    // antes de começar a ditar campos é o que diz à pessoa onde ela está.
    caixa.current?.focus()
    // A página atrás não rola junto, senão fechar o painel devolve a lista
    // num ponto diferente de onde ela estava.
    const rolagem = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', tecla)
      document.body.style.overflow = rolagem
      voltarPara.current?.focus?.()
    }
  }, [tecla])

  return (
    <div className="painel">
      {/* Botão e não div: clicar fora para fechar precisa ser alcançável por
          quem não usa mouse, e um div com onClick não é. */}
      <button
        type="button"
        className="painel__veu"
        aria-label="Fechar sem salvar"
        onClick={aoFechar}
      />
      <div
        className="painel__caixa"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        ref={caixa}
      >
        <header className="painel__topo">
          <div>
            <h2 className="painel__titulo">{titulo}</h2>
            {nota && <p className="field__hint">{nota}</p>}
          </div>
          {/* Sem ícone: o conjunto não tem um "X", e emprestar o triângulo de
              aviso diria que fechar é perigoso. A palavra basta. */}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={aoFechar}
            title="Fechar sem salvar (Esc)"
          >
            Fechar
          </button>
        </header>
        <div className="painel__corpo">{children}</div>
      </div>
    </div>
  )
}
